import React, { useState, useEffect, useRef } from "react";
import { BrowserRouter as Router, Route, Switch, Redirect, useHistory, NavLink, Link } from "react-router-dom";
import { toast } from "react-hot-toast";
import {
  HiExternalLink,
  HiOutlineLogout,
  HiOutlineMenu,
  HiOutlineX,
  HiOutlineSearch,
  HiChevronDown,
  HiOutlineCog,
  HiOutlineBookOpen,
  HiOutlineQuestionMarkCircle,
  HiOutlineOfficeBuilding,
  HiOutlineGlobeAlt,
  HiOutlineDesktopComputer,
  HiOutlineRefresh,
  HiOutlineDocumentText,
  HiOutlineClipboardList,
  HiOutlineChartBar,
  HiOutlineCollection,
  HiOutlineLightBulb,
  HiOutlineUserGroup,
  HiOutlineTag,
  HiOutlineBeaker,
  HiOutlineLightningBolt,
  HiOutlineInbox,
  HiOutlineLockClosed,
  HiOutlineArrowRight,
} from "react-icons/hi";
import { useQuery } from "@tanstack/react-query";
import { PiClockCounterClockwise } from "react-icons/pi";
import { useHotkeys } from "react-hotkeys-hook";
import CommandPalette from "./components/CommandPalette";
import useCommandPalette from "./utils/useCommandPalette";
import GlobalShortcuts from "./components/shortcuts/GlobalShortcuts";
import ShortcutHint from "./components/shortcuts/ShortcutHint";
import { SHORTCUTS } from "./utils/shortcuts";
import { notifyShortcut } from "./utils/shortcutTip";
import TaskSidebar from "./components/TaskSidebar";
import RealtimeBanner from "./components/RealtimeBanner";
import BetaBanner from "./components/BetaBanner";
import { SocketProvider } from "./contexts/SocketContext";
import { AdmissionsProvider } from "./contexts/AdmissionsContext";
import { FaBell, FaBellSlash } from "react-icons/fa";
import API from "./api";
import { clearListStates } from "./utils";
import { LinearLoadingOverlay } from "./components/Loading";
import { capture, identify } from "./instrument";
import { DWT_CONTAINER_ID } from "./utils/dynamsoftWebTwain";

import Auth from "./scenes/auth";
import ResetPassword from "./scenes/resetPassword";
import ChangePassword from "./scenes/changePassword";
import AnnotationFolders from "./scenes/annotation";
import AnnotationEditor from "./scenes/annotation/editor";
import AdmissionsList from "./scenes/admissions";
import AdmissionDetail from "./scenes/admissions/detail";
import Table from "./scenes/table/index";
import AddTable from "./scenes/table/add";
import EditTable from "./scenes/table/edit";
import HTR from "./scenes/htr";
import Stats from "./scenes/stats";
import Prescriptions from "./scenes/ordonnance/index";
import PrescriptionDetail from "./scenes/ordonnance/detail";
import Settings from "./scenes/settings";
import { getSetting } from "./queries/setting";
import Sandbox from "./scenes/sandbox";
import Question from "./scenes/question/index";
import AddQuestion from "./scenes/question/add";
import EditQuestion from "./scenes/question/edit";
import Laboratory from "./scenes/laboratory/index";
import AddLaboratory from "./scenes/laboratory/add";
import EditLaboratory from "./scenes/laboratory/edit";
import Network from "./scenes/network/index";
import AddNetwork from "./scenes/network/add";
import EditNetwork from "./scenes/network/edit";
import Guidelines from "./scenes/guideline";
import AddGuideline from "./scenes/guideline/add";
import EditGuideline from "./scenes/guideline/edit";
import Machine from "./scenes/machine/index";
import MachineLogs from "./scenes/machine/logs";
import Sync from "./scenes/sync/index";
import AuditLog from "./scenes/auditLog/index";
import AddClinic from "./scenes/clinic/add";
import EditClinic from "./scenes/clinic/edit";
import Clinic from "./scenes/clinic/index";
import Code from "./scenes/code/index";
import EditCode from "./scenes/code/edit";
import AddCode from "./scenes/code/add";
import Simulator from "./scenes/simulator/index";
import Actions from "./scenes/action/index";
import AddAction from "./scenes/action/add";
import EditAction from "./scenes/action/edit";

const App = () => {
  const [loading, setLoading] = useState(true);

  useEffect(() => API.onTokenChange(clearListStates), []);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const res = await API.get("/user/signin_token");
        if (!res.ok || !res.user || !res.token) return;

        API.setToken(res.token);
      } catch (e) {
        capture(e);
        if (e.code !== 401) toast.error(e.message);
      }

      setLoading(false);
    })();
  }, []);

  useEffect(() => {
    ((w) => {
      const P = (w.Productlane = { queue: {} });
      ["set", "open", "close", "toggle", "on", "off", "init", "enable", "disable"].forEach((m) => {
        P[m] = ((n) =>
          function () {
            P.queue[n] = { args: arguments };
          })(m);
      });
    })(window);

    window.Productlane.init({ widgetKey: "f2939f7c-a10c-40c5-aa32-b4d68fa765b8" });

    const script = document.createElement("script");
    script.src = "https://widget.productlane.com/latest.productlane-widget.min.js";
    script.async = true;
    script.defer = true;
    script.crossOrigin = "anonymous";
    document.body.appendChild(script);

    return () => script.remove();
  }, []);

  if (loading)
    return (
      <div className="flex-center h-screen w-full">
        <LinearLoadingOverlay isVisible={true} message="Chargement de l'application..." />
      </div>
    );

  return (
    <Router>
      <UnauthorizedRedirect />
      <SocketProvider>
        <AdmissionsProvider>
          <Switch>
            <Route path="/auth" component={Auth} />
            <Route path="/reset-password" component={ResetPassword} />
            <Route path="/change-password" component={ChangePassword} />
            <RestrictedRoute path="/annotation" exact component={AnnotationFolders} role="superadmin" />
            <RestrictedRoute path="/annotation/:folderId/:annotationId?" component={AnnotationEditor} role="superadmin" />
            <RestrictedRoute path="/admissions" exact component={AdmissionsList} moduleGate="admission" />
            <RestrictedRoute path="/admissions/:id" component={AdmissionDetail} moduleGate="admission" />
            <RestrictedRoute path="/htr" component={HTR} role="superadmin" />
            <RestrictedRoute path="/action" exact component={Actions} role="admin" />
            <RestrictedRoute path="/action/add" component={AddAction} role="admin" />
            <RestrictedRoute path="/action/:id/edit" component={EditAction} role="admin" />
            <RestrictedRoute path="/guideline" exact component={Guidelines} moduleGate="any" role="admin" />
            <RestrictedRoute path="/guideline/add" component={AddGuideline} moduleGate="any" role="admin" />
            <RestrictedRoute path="/guideline/:id/edit" component={EditGuideline} moduleGate="any" role="admin" />
            <RestrictedRoute path="/question/:id/edit" component={EditQuestion} moduleGate="any" role="admin" />
            <RestrictedRoute path="/question/add" component={AddQuestion} moduleGate="any" role="admin" />
            <RestrictedRoute path="/question" component={Question} moduleGate="any" role="admin" />
            <RestrictedRoute path="/laboratory" exact component={Laboratory} moduleGate="any" role="admin" />
            <RestrictedRoute path="/laboratory/add" component={AddLaboratory} moduleGate="any" role="admin" />
            <RestrictedRoute path="/laboratory/:id/edit" component={EditLaboratory} moduleGate="any" role="admin" />
            <RestrictedRoute path="/network" exact component={Network} role="superadmin" />
            <RestrictedRoute path="/network/add" component={AddNetwork} role="superadmin" />
            <RestrictedRoute path="/network/:id/edit" component={EditNetwork} role="superadmin" />
            <RestrictedRoute path="/machine/:streamName/logs" component={MachineLogs} moduleGate="any" />
            <RestrictedRoute path="/machine" exact component={Machine} moduleGate="any" role="admin" />
            <RestrictedRoute path="/sync" component={Sync} role="admin" />
            <RestrictedRoute path="/audit-log/:collectionName?/:documentId?" component={AuditLog} role="admin" />
            <RestrictedRoute path="/code/:id/edit" component={EditCode} role="admin" />
            <RestrictedRoute path="/code/add" component={AddCode} role="admin" />
            <RestrictedRoute path="/code" exact component={Code} role="admin" />
            <RestrictedRoute path="/table/:id/edit" component={EditTable} role="admin" />
            <RestrictedRoute path="/table/add" component={AddTable} role="admin" />
            <RestrictedRoute path="/table" exact component={Table} role="admin" />
            <RestrictedRoute path="/prescription/:id" component={PrescriptionDetail} />
            <RestrictedRoute path="/prescription" exact component={Prescriptions} />
            <RestrictedRoute path="/clinic" exact component={Clinic} moduleGate="ide" role="admin" />
            <RestrictedRoute path="/clinic/add" component={AddClinic} moduleGate="ide" role="admin" />
            <RestrictedRoute path="/clinic/:id/edit" component={EditClinic} moduleGate="ide" role="admin" />
            <RestrictedRoute path="/settings/:section?" component={Settings} role="admin" />
            <RestrictedRoute path="/simulator" component={Simulator} role="admin" />
            <RestrictedRoute path="/querco/sandbox" component={Sandbox} setLoading={setLoading} role="superadmin" />
            <RestrictedRoute path="/" exact component={Stats} role="admin" />
          </Switch>
        </AdmissionsProvider>
      </SocketProvider>
      <div id={DWT_CONTAINER_ID} className="hidden" aria-hidden="true" />
    </Router>
  );
};

const UnauthorizedRedirect = () => {
  const history = useHistory();

  useEffect(() => {
    const handleUnauthorized = () => {
      const { pathname, search, hash } = history.location;
      const redirectPath = `${pathname}${search}${hash}`;

      API.setToken("");

      if (pathname === "/auth") return;

      toast.error("Votre session a expiré, veuillez vous reconnecter.");

      const shouldRedirectBack = pathname !== "/reset-password" && pathname !== "/auth";
      history.replace({
        pathname: "/auth",
        search: shouldRedirectBack && redirectPath ? `?redirect=${encodeURIComponent(redirectPath)}` : "",
      });
    };

    API.setUnauthorizedHandler(handleUnauthorized);
    return () => {
      API.setUnauthorizedHandler(null);
    };
  }, [history]);

  return null;
};

const isModuleAllowed = (moduleGate, moduleEnabled) => {
  if (!moduleGate) return true;
  if (moduleGate === "admission") return moduleEnabled.admission === true;
  if (moduleGate === "ide") return moduleEnabled.ide === true;
  if (moduleGate === "any") return moduleEnabled.admission === true || moduleEnabled.ide === true;
  return true;
};

const MODULE_INFO = {
  admission: {
    name: "Admissions",
    description:
      "Transformez l'expérience d'admission : vos patients remplissent eux-mêmes leur dossier avant d'arriver. Moins de files d'attente, moins d'erreurs de saisie, plus de temps pour votre équipe.",
  },
  ide: {
    name: "IDE",
    description:
      "Équipez vos infirmiers d'une application mobile pour créer les admissions directement au chevet du patient. Fini le papier et la double saisie — un flux fluide, du domicile au laboratoire.",
  },
  any: {
    name: "Configuration avancée",
    description:
      "Débloquez la configuration complète de votre compte : questions personnalisées, recommandations automatiques, laboratoires et machines. Adaptez Querco à votre organisation.",
  },
};

const BlockedPage = ({ moduleGate }) => {
  const info = MODULE_INFO[moduleGate] || MODULE_INFO.any;

  return (
    <div className="flex min-h-[calc(100vh-56px)] items-center justify-center bg-gradient-to-br from-slate-50 via-white to-primary-50/40 px-4">
      <div className="max-w-lg text-center">
        <div className="relative mx-auto h-20 w-20">
          <div className="absolute inset-0 rounded-full bg-primary-200/40 opacity-70 blur-xl" />
          <div className="relative flex h-full w-full items-center justify-center rounded-full bg-white shadow-lg ring-1 ring-primary-100">
            <HiOutlineLockClosed className="h-9 w-9 text-primary-600" />
          </div>
        </div>
        <span className="mt-8 inline-flex items-center gap-1.5 rounded-full bg-primary-50 px-3 py-1 text-xs font-semibold uppercase tracking-wide text-primary-700 ring-1 ring-inset ring-primary-100">
          Module verrouillé
        </span>
        <h1 className="mt-5 text-4xl font-bold tracking-tight text-gray-900">{info.name}</h1>
        <p className="mx-auto mt-3 max-w-md text-base leading-7 text-gray-600">{info.description}</p>
        <div className="mt-8 flex flex-col items-center gap-3 sm:flex-row sm:justify-center">
          <a
            href="mailto:support@querco.co"
            className="inline-flex items-center gap-2 rounded-xl bg-gray-900 px-6 py-3 text-sm font-semibold text-white shadow-sm transition-all hover:bg-gray-800 hover:shadow-md"
          >
            Activer ce module
            <HiOutlineArrowRight className="h-4 w-4" />
          </a>
          <Link to="/" className="text-sm font-medium text-gray-600 transition-colors hover:text-gray-900">
            Retour au tableau de bord
          </Link>
        </div>
      </div>
    </div>
  );
};

const RestrictedRoute = ({ component: Component, setLoading, role, moduleGate, ...rest }) => {
  const redirect = encodeURIComponent(window.location.href.replace(window.location.origin, ""));
  const history = useHistory();
  const [user, setUser] = useState(null);
  const [sidebarOpen, setSidebarOpen] = useState(() => localStorage.getItem("taskSidebarOpen") === "true");

  const { data: setting, isLoading: isSettingLoading } = useQuery({
    queryKey: ["setting"],
    queryFn: getSetting,
    enabled: !!API.getToken(),
  });

  const moduleEnabled = {
    admission: setting?.moduleEnabled?.admission === true,
    ide: setting?.moduleEnabled?.ide === true,
  };

  const getCurrentUser = async () => {
    try {
      const { data } = await API.get("/user/me");

      if (!data) return;

      setUser(data);
      identify(data.username || data.email, data.indexName);
    } catch (error) {
      capture(error);
      if (error.code !== 401) {
        toast.error("Une erreur est survenue");
        API.setToken("");
        history.push("/auth");
      }
    }
  };

  useEffect(() => {
    getCurrentUser();
  }, []);

  const toggleSidebar = () => {
    setSidebarOpen((prev) => {
      const next = !prev;
      localStorage.setItem("taskSidebarOpen", String(next));
      return next;
    });
  };

  const logout = async () => {
    try {
      await API.post("/user/logout");
      API.setToken("");
      history.push("/");
    } catch (error) {
      capture(error);
      toast.error(error.message);
    }
  };

  let render = (props) => {
    if (!user || (moduleGate && isSettingLoading)) {
      return (
        <div className="flex-center h-screen w-full">
          <LinearLoadingOverlay isVisible={true} message="Chargement..." />
        </div>
      );
    }

    if (user.mustChangePassword) return <Redirect to="/change-password" />;

    if (role === "superadmin" && user.role !== "superadmin") return <Redirect to="/admissions" />;
    if (role === "admin" && user.role !== "admin" && user.role !== "superadmin") return <Redirect to="/admissions" />;

    const canSeeTasks = user.role === "admin" || user.role === "superadmin";

    return (
      <div className="flex min-h-screen flex-col">
        <Header user={user} logout={logout} sidebarOpen={sidebarOpen} toggleSidebar={toggleSidebar} />
        {canSeeTasks && <TaskSidebar isOpen={sidebarOpen} onClose={toggleSidebar} />}
        <div className={`flex-1 transition-all duration-200 ${canSeeTasks && sidebarOpen ? "lg:mr-96" : ""}`}>
          {isModuleAllowed(moduleGate, moduleEnabled) ? <Component {...props} setLoading={setLoading} user={user} /> : <BlockedPage moduleGate={moduleGate} />}
        </div>
      </div>
    );
  };

  if (!API.getToken()) {
    render = (props) => (
      <Redirect
        to={{
          search: redirect ? `?redirect=${redirect}` : "",
          pathname: "/auth",
        }}
      />
    );
  }

  return <Route {...rest} render={render} />;
};

const TaskBadge = () => {
  const { data } = useQuery({
    queryKey: ["taskCount"],
    queryFn: async () => {
      const response = await API.get("/task/count");
      if (!response.ok) throw new Error(response.message);
      return response.data;
    },
    refetchInterval: 60_000,
  });

  const count = data?.count || 0;
  if (count === 0) return null;

  return (
    <span className="absolute -right-1 -top-1 flex h-[18px] min-w-[18px] items-center justify-center rounded-full bg-red-500 px-1 text-[10px] font-bold text-white">
      {count > 99 ? "99+" : count}
    </span>
  );
};

const Header = ({ user, logout, sidebarOpen, toggleSidebar }) => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [configMenuOpen, setConfigMenuOpen] = useState(false);
  const configMenuRef = useRef(null);
  const { isCommandPaletteOpen, openCommandPalette, closeCommandPalette } = useCommandPalette();
  useHotkeys(SHORTCUTS.TOGGLE_SIDEBAR.keys, toggleSidebar, { preventDefault: true, enabled: user.role === "admin" || user.role === "superadmin" });
  const [notificationsEnabled, setNotificationsEnabled] = useState(false);
  const [swRegistration, setSwRegistration] = useState(null);

  useEffect(() => {
    initNotifications();
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (configMenuRef.current && !configMenuRef.current.contains(event.target)) {
        setConfigMenuOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const initNotifications = async () => {
    try {
      if (!("Notification" in window) || !("serviceWorker" in navigator) || !("PushManager" in window)) {
        return;
      }

      if (Notification.permission === "denied") {
        return toast.error("Les notifications ont été bloquées. Veuillez les activer dans les paramètres de votre navigateur.");
      }

      const permission = await Notification.requestPermission();
      if (permission !== "granted") {
        return toast.error("Veuillez activer les notifications pour recevoir des notifications");
      }

      let registration = await navigator.serviceWorker.getRegistration();
      if (!registration) registration = await navigator.serviceWorker.register("/service-worker.js");

      if (!registration.active) {
        await new Promise((resolve) => {
          const interval = setInterval(() => {
            if (registration.active) {
              clearInterval(interval);
              resolve();
            }
          }, 100);
        });
      }

      if (!registration) return toast.error("Une erreur est survenue lors de l'initialisation des notifications");

      setSwRegistration(registration);
      const lastNotificationsStatus = localStorage.getItem("notificationsEnabled");
      setNotificationsEnabled(lastNotificationsStatus === "true");
    } catch (error) {
      capture(error);
      console.error("Failed to manage push notifications:", error);
      toast.error("Une erreur est survenue lors de l'initialisation des notifications");
    }
  };

  const toggleNotifications = async (status) => {
    const newStatus = !status;
    if (!swRegistration) {
      toast.error("Service Worker registration is not available. Please try again later.");
      return;
    }

    try {
      setNotificationsEnabled(newStatus);

      if (newStatus === false) {
        const subscription = await swRegistration.pushManager.getSubscription();
        if (subscription) await subscription.unsubscribe();
        setNotificationsEnabled(false);
        localStorage.setItem("notificationsEnabled", "false");
        return toast("Notifications désactivées", { icon: "🔕" });
      }

      let pushSubscription = await swRegistration.pushManager.getSubscription();
      if (!pushSubscription) {
        const subscribeOptions = {
          userVisibleOnly: true,
          applicationServerKey: "BJ_3oN9cCQ5oGJaRIXrzqyuJvIHnC1eJxPFithPs3OMybUCyHLRfpoeA-TvVyyYMuBqwv8Bjbwki6_IxW7tlxbU",
        };

        pushSubscription = await swRegistration.pushManager.subscribe(subscribeOptions);
      }

      const { ok } = await API.post("/push-notification", { subscription: pushSubscription });
      if (!ok) throw new Error("Failed to store push subscription");

      setNotificationsEnabled(true);
      localStorage.setItem("notificationsEnabled", "true");
      toast("Notifications activées", { icon: "🔔" });
    } catch (error) {
      capture(error);
      console.error("Failed to manage push notifications:", error);

      setNotificationsEnabled(status);
      localStorage.setItem("notificationsEnabled", status === true ? "true" : "false");

      if (error.name === "NotAllowedError") {
        toast.error("L'accès aux notifications a été refusé");
      } else {
        toast.error("Une erreur est survenue lors de la gestion des notifications");
      }
    }
  };

  return (
    <>
      <CommandPalette isOpen={isCommandPaletteOpen} onClose={closeCommandPalette} user={user} logout={logout} />
      <GlobalShortcuts user={user} />

      <div className="sticky top-0 z-50 border-b border-gray-950/[0.08] bg-white/70 backdrop-blur-xl backdrop-saturate-150">
        <div className="flex h-14 items-center justify-between px-4">
          <div className="flex items-center gap-8">
            <Link to="/" className="flex items-center gap-2 text-lg font-semibold text-primary-500">
              <div className="flex h-7 w-7 items-center justify-center rounded-lg bg-primary-500">
                <span className="text-sm font-bold text-white">Q</span>
              </div>
              Extraction
            </Link>

            <nav className="hidden items-center gap-1 lg:flex">
              {(user.role === "admin" || user.role === "superadmin") && (
                <NavLink
                  to="/"
                  exact
                  className="rounded-md px-3 py-1.5 text-sm text-gray-600 transition-colors hover:bg-gray-100 hover:text-gray-900"
                  activeClassName="!text-primary-600 !bg-primary-50 font-medium"
                >
                  <HiOutlineChartBar className="-mt-0.5 mr-1.5 inline-block h-4 w-4" />
                  Stats
                </NavLink>
              )}

              <NavLink
                to="/admissions"
                className="rounded-md px-3 py-1.5 text-sm text-gray-600 transition-colors hover:bg-gray-100 hover:text-gray-900"
                activeClassName="!text-primary-600 !bg-primary-50 font-medium"
              >
                <HiOutlineClipboardList className="-mt-0.5 mr-1.5 inline-block h-4 w-4" />
                Admissions
              </NavLink>

              <NavLink
                to="/prescription"
                className="rounded-md px-3 py-1.5 text-sm text-gray-600 transition-colors hover:bg-gray-100 hover:text-gray-900"
                activeClassName="!text-primary-600 !bg-primary-50 font-medium"
              >
                <HiOutlineDocumentText className="-mt-0.5 mr-1.5 inline-block h-4 w-4" />
                Ordonnances
              </NavLink>

              {(user.role === "admin" || user.role === "superadmin") && (
                <NavLink
                  to="/table"
                  className="rounded-md px-3 py-1.5 text-sm text-gray-600 transition-colors hover:bg-gray-100 hover:text-gray-900"
                  activeClassName="!text-primary-600 !bg-primary-50 font-medium"
                >
                  <HiOutlineCollection className="-mt-0.5 mr-1.5 inline-block h-4 w-4" />
                  Table
                </NavLink>
              )}

              {(user.role === "admin" || user.role === "superadmin") && (
                <div className="relative" ref={configMenuRef}>
                  <button
                    type="button"
                    onClick={() => setConfigMenuOpen(!configMenuOpen)}
                    className={`flex items-center gap-1 rounded-md px-3 py-1.5 text-sm text-gray-600 transition-colors hover:bg-gray-100 hover:text-gray-900 ${
                      configMenuOpen ? "bg-gray-100" : ""
                    }`}
                  >
                    <HiOutlineCog className="h-4 w-4" />
                    Configuration
                    <HiChevronDown className={`h-3.5 w-3.5 transition-transform ${configMenuOpen ? "rotate-180" : ""}`} />
                  </button>

                  {configMenuOpen && (
                    <div className="absolute left-0 top-full z-50 mt-1 w-56 rounded-lg border border-gray-200 bg-white py-1 shadow-lg">
                      <NavLink
                        to="/action"
                        onClick={() => setConfigMenuOpen(false)}
                        className="flex items-center gap-3 px-4 py-2.5 text-sm text-gray-700 transition-colors hover:bg-gray-50"
                        activeClassName="!text-primary-600 !bg-primary-50"
                      >
                        <HiOutlineLightningBolt className="h-4 w-4 text-gray-400" />
                        Actions
                      </NavLink>
                      <NavLink
                        to="/guideline"
                        onClick={() => setConfigMenuOpen(false)}
                        className="flex items-center gap-3 px-4 py-2.5 text-sm text-gray-700 transition-colors hover:bg-gray-50"
                        activeClassName="!text-primary-600 !bg-primary-50"
                      >
                        <HiOutlineLightBulb className="h-4 w-4 text-gray-400" />
                        Recommandations
                      </NavLink>
                      <NavLink
                        to="/question"
                        onClick={() => setConfigMenuOpen(false)}
                        className="flex items-center gap-3 px-4 py-2.5 text-sm text-gray-700 transition-colors hover:bg-gray-50"
                        activeClassName="!text-primary-600 !bg-primary-50"
                      >
                        <HiOutlineQuestionMarkCircle className="h-4 w-4 text-gray-400" />
                        Questions
                      </NavLink>
                      <NavLink
                        to="/laboratory"
                        onClick={() => setConfigMenuOpen(false)}
                        className="flex items-center gap-3 px-4 py-2.5 text-sm text-gray-700 transition-colors hover:bg-gray-50"
                        activeClassName="!text-primary-600 !bg-primary-50"
                      >
                        <HiOutlineOfficeBuilding className="h-4 w-4 text-gray-400" />
                        Laboratoires
                      </NavLink>
                      <NavLink
                        to="/clinic"
                        onClick={() => setConfigMenuOpen(false)}
                        className="flex items-center gap-3 px-4 py-2.5 text-sm text-gray-700 transition-colors hover:bg-gray-50"
                        activeClassName="!text-primary-600 !bg-primary-50"
                      >
                        <HiOutlineUserGroup className="h-4 w-4 text-gray-400" />
                        Cliniques
                      </NavLink>
                      <NavLink
                        to="/code"
                        onClick={() => setConfigMenuOpen(false)}
                        className="flex items-center gap-3 px-4 py-2.5 text-sm text-gray-700 transition-colors hover:bg-gray-50"
                        activeClassName="!text-primary-600 !bg-primary-50"
                      >
                        <HiOutlineTag className="h-4 w-4 text-gray-400" />
                        Codes
                      </NavLink>
                      <NavLink
                        to="/machine"
                        onClick={() => setConfigMenuOpen(false)}
                        className="flex items-center gap-3 px-4 py-2.5 text-sm text-gray-700 transition-colors hover:bg-gray-50"
                        activeClassName="!text-primary-600 !bg-primary-50"
                      >
                        <HiOutlineDesktopComputer className="h-4 w-4 text-gray-400" />
                        Machines
                      </NavLink>
                      <NavLink
                        to="/sync"
                        onClick={() => setConfigMenuOpen(false)}
                        className="flex items-center gap-3 px-4 py-2.5 text-sm text-gray-700 transition-colors hover:bg-gray-50"
                        activeClassName="!text-primary-600 !bg-primary-50"
                      >
                        <HiOutlineRefresh className="h-4 w-4 text-gray-400" />
                        Synchronisation
                      </NavLink>
                      {user?.role === "superadmin" && (
                        <NavLink
                          to="/network"
                          onClick={() => setConfigMenuOpen(false)}
                          className="flex items-center gap-3 px-4 py-2.5 text-sm text-gray-700 transition-colors hover:bg-gray-50"
                          activeClassName="!text-primary-600 !bg-primary-50"
                        >
                          <HiOutlineGlobeAlt className="h-4 w-4 text-gray-400" />
                          Réseaux
                        </NavLink>
                      )}
                      <div className="my-1 border-t border-gray-100" />
                      <NavLink
                        to="/simulator"
                        onClick={() => setConfigMenuOpen(false)}
                        className="flex items-center gap-3 px-4 py-2.5 text-sm text-gray-700 transition-colors hover:bg-gray-50"
                        activeClassName="!text-primary-600 !bg-primary-50"
                      >
                        <HiOutlineBeaker className="h-4 w-4 text-gray-400" />
                        Simulateur
                      </NavLink>
                      <div className="my-1 border-t border-gray-100" />
                      <NavLink
                        to="/audit-log"
                        onClick={() => setConfigMenuOpen(false)}
                        className="flex items-center gap-3 px-4 py-2.5 text-sm text-gray-700 transition-colors hover:bg-gray-50"
                        activeClassName="!text-primary-600 !bg-primary-50"
                      >
                        <PiClockCounterClockwise className="h-4 w-4 text-gray-400" />
                        Journal d'audit
                      </NavLink>
                      <NavLink
                        to="/settings"
                        onClick={() => setConfigMenuOpen(false)}
                        className="flex items-center gap-3 px-4 py-2.5 text-sm text-gray-700 transition-colors hover:bg-gray-50"
                        activeClassName="!text-primary-600 !bg-primary-50"
                      >
                        <HiOutlineCog className="h-4 w-4 text-gray-400" />
                        Paramètres
                      </NavLink>
                    </div>
                  )}
                </div>
              )}

              {user.role === "superadmin" && <div className="rounded-full bg-red-50 px-2 py-1 text-xs font-medium text-red-600">Index: {user?.indexName}</div>}
            </nav>
          </div>

          <div className="hidden items-center gap-3 lg:flex">
            <button
              type="button"
              onClick={openCommandPalette}
              className="flex items-center gap-2 rounded-lg border border-gray-200 bg-gray-50 px-3 py-1.5 text-sm text-gray-500 transition-colors hover:bg-gray-100 hover:text-gray-700"
            >
              <HiOutlineSearch className="h-4 w-4" />
              <span className="hidden xl:inline">Rechercher</span>
              <ShortcutHint id="TOGGLE_PALETTE" className="hidden xl:inline-flex" />
            </button>

            <button
              type="button"
              onClick={() => window.open("https://help.querco.co/", "_blank")}
              className="rounded-lg p-2 text-gray-500 transition-colors hover:bg-gray-100 hover:text-gray-700"
              title="Manuel d'utilisation"
            >
              <HiOutlineBookOpen className="h-5 w-5" />
            </button>

            <button
              className="rounded-lg p-2 transition-colors hover:bg-gray-100"
              onClick={() => toggleNotifications(notificationsEnabled)}
              title={notificationsEnabled ? "Notifications activées" : "Activer les notifications"}
            >
              {notificationsEnabled ? <FaBell className="h-4 w-4 text-primary-500" /> : <FaBellSlash className="h-4 w-4 text-gray-400" />}
            </button>

            {(user.role === "admin" || user.role === "superadmin") && (
              <button
                type="button"
                onClick={() => {
                  toggleSidebar();
                  notifyShortcut("TOGGLE_SIDEBAR");
                }}
                className={`relative rounded-lg p-2 transition-colors ${sidebarOpen ? "bg-primary-50 text-primary-600" : "text-gray-500 hover:bg-gray-100 hover:text-gray-700"}`}
                title="Taches"
              >
                <HiOutlineInbox className="h-5 w-5" />
                <TaskBadge />
              </button>
            )}

            <div className="h-6 w-px bg-gray-200" />

            <button type="button" onClick={logout} className="rounded-lg p-2 text-gray-500 transition-colors hover:bg-red-50 hover:text-red-600" title="Se déconnecter">
              <HiOutlineLogout className="h-5 w-5" />
            </button>
          </div>

          <div className="flex items-center gap-2 lg:hidden">
            {(user.role === "admin" || user.role === "superadmin") && (
              <button
                type="button"
                onClick={() => {
                  toggleSidebar();
                  notifyShortcut("TOGGLE_SIDEBAR");
                }}
                className={`relative rounded-lg p-2 transition-colors ${sidebarOpen ? "bg-primary-50 text-primary-600" : "text-gray-500 hover:bg-gray-100 hover:text-gray-700"}`}
                title="Taches"
              >
                <HiOutlineInbox className="h-5 w-5" />
                <TaskBadge />
              </button>
            )}
            <button type="button" className="rounded-lg p-2 text-gray-600 hover:bg-gray-100 hover:text-gray-900" onClick={() => setMobileMenuOpen(!mobileMenuOpen)}>
              {mobileMenuOpen ? <HiOutlineX className="h-6 w-6" /> : <HiOutlineMenu className="h-6 w-6" />}
            </button>
          </div>
        </div>

        {mobileMenuOpen && (
          <div className="border-t border-gray-100 bg-white lg:hidden">
            <div className="space-y-1 px-4 py-3">
              {(user.role === "admin" || user.role === "superadmin") && (
                <NavLink
                  to="/"
                  exact
                  onClick={() => setMobileMenuOpen(false)}
                  className="flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm text-gray-700 hover:bg-gray-50"
                  activeClassName="!text-primary-600 !bg-primary-50 font-medium"
                >
                  <HiOutlineChartBar className="h-5 w-5" />
                  Statistiques
                </NavLink>
              )}

              <NavLink
                to="/admissions"
                onClick={() => setMobileMenuOpen(false)}
                className="flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm text-gray-700 hover:bg-gray-50"
                activeClassName="!text-primary-600 !bg-primary-50 font-medium"
              >
                <HiOutlineClipboardList className="h-5 w-5" />
                Admissions
              </NavLink>

              <NavLink
                to="/prescription"
                onClick={() => setMobileMenuOpen(false)}
                className="flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm text-gray-700 hover:bg-gray-50"
                activeClassName="!text-primary-600 !bg-primary-50 font-medium"
              >
                <HiOutlineDocumentText className="h-5 w-5" />
                Ordonnances
              </NavLink>

              {(user.role === "admin" || user.role === "superadmin") && (
                <NavLink
                  to="/table"
                  onClick={() => setMobileMenuOpen(false)}
                  className="flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm text-gray-700 hover:bg-gray-50"
                  activeClassName="!text-primary-600 !bg-primary-50 font-medium"
                >
                  <HiOutlineCollection className="h-5 w-5" />
                  Table
                </NavLink>
              )}

              {(user.role === "admin" || user.role === "superadmin") && (
                <>
                  <div className="pb-1 pt-2">
                    <div className="px-3 text-xs font-medium uppercase tracking-wider text-gray-400">Configuration</div>
                  </div>

                  <NavLink
                    to="/action"
                    onClick={() => setMobileMenuOpen(false)}
                    className="flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm text-gray-700 hover:bg-gray-50"
                    activeClassName="!text-primary-600 !bg-primary-50 font-medium"
                  >
                    <HiOutlineLightningBolt className="h-5 w-5" />
                    Actions
                  </NavLink>

                  <NavLink
                    to="/guideline"
                    onClick={() => setMobileMenuOpen(false)}
                    className="flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm text-gray-700 hover:bg-gray-50"
                    activeClassName="!text-primary-600 !bg-primary-50 font-medium"
                  >
                    <HiOutlineLightBulb className="h-5 w-5" />
                    Recommandations
                  </NavLink>

                  <NavLink
                    to="/question"
                    onClick={() => setMobileMenuOpen(false)}
                    className="flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm text-gray-700 hover:bg-gray-50"
                    activeClassName="!text-primary-600 !bg-primary-50 font-medium"
                  >
                    <HiOutlineQuestionMarkCircle className="h-5 w-5" />
                    Questions
                  </NavLink>

                  <NavLink
                    to="/laboratory"
                    onClick={() => setMobileMenuOpen(false)}
                    className="flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm text-gray-700 hover:bg-gray-50"
                    activeClassName="!text-primary-600 !bg-primary-50 font-medium"
                  >
                    <HiOutlineOfficeBuilding className="h-5 w-5" />
                    Laboratoires
                  </NavLink>

                  <NavLink
                    to="/clinic"
                    onClick={() => setMobileMenuOpen(false)}
                    className="flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm text-gray-700 hover:bg-gray-50"
                    activeClassName="!text-primary-600 !bg-primary-50 font-medium"
                  >
                    <HiOutlineUserGroup className="h-5 w-5" />
                    Cliniques
                  </NavLink>

                  <NavLink
                    to="/code"
                    onClick={() => setMobileMenuOpen(false)}
                    className="flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm text-gray-700 hover:bg-gray-50"
                    activeClassName="!text-primary-600 !bg-primary-50 font-medium"
                  >
                    <HiOutlineTag className="h-5 w-5" />
                    Codes
                  </NavLink>

                  <NavLink
                    to="/machine"
                    onClick={() => setMobileMenuOpen(false)}
                    className="flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm text-gray-700 hover:bg-gray-50"
                    activeClassName="!text-primary-600 !bg-primary-50 font-medium"
                  >
                    <HiOutlineDesktopComputer className="h-5 w-5" />
                    Machines
                  </NavLink>

                  <NavLink
                    to="/sync"
                    onClick={() => setMobileMenuOpen(false)}
                    className="flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm text-gray-700 hover:bg-gray-50"
                    activeClassName="!text-primary-600 !bg-primary-50 font-medium"
                  >
                    <HiOutlineRefresh className="h-5 w-5" />
                    Synchronisation
                  </NavLink>

                  <NavLink
                    to="/simulator"
                    onClick={() => setMobileMenuOpen(false)}
                    className="flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm text-gray-700 hover:bg-gray-50"
                    activeClassName="!text-primary-600 !bg-primary-50 font-medium"
                  >
                    <HiOutlineBeaker className="h-5 w-5" />
                    Simulateur
                  </NavLink>

                  <NavLink
                    to="/audit-log"
                    onClick={() => setMobileMenuOpen(false)}
                    className="flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm text-gray-700 hover:bg-gray-50"
                    activeClassName="!text-primary-600 !bg-primary-50 font-medium"
                  >
                    <PiClockCounterClockwise className="h-5 w-5" />
                    Journal d'audit
                  </NavLink>

                  <NavLink
                    to="/settings"
                    onClick={() => setMobileMenuOpen(false)}
                    className="flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm text-gray-700 hover:bg-gray-50"
                    activeClassName="!text-primary-600 !bg-primary-50 font-medium"
                  >
                    <HiOutlineCog className="h-5 w-5" />
                    Paramètres
                  </NavLink>
                </>
              )}

              <div className="my-2 border-t border-gray-100" />

              <button
                type="button"
                onClick={() => {
                  window.open("https://help.querco.co/", "_blank");
                  setMobileMenuOpen(false);
                }}
                className="flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-sm text-gray-700 hover:bg-gray-50"
              >
                <HiExternalLink className="h-5 w-5" />
                Manuel d'utilisation
              </button>

              <button
                type="button"
                onClick={() => {
                  logout();
                  setMobileMenuOpen(false);
                }}
                className="flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-sm text-red-600 hover:bg-red-50"
              >
                <HiOutlineLogout className="h-5 w-5" />
                Se déconnecter
              </button>
            </div>
          </div>
        )}
        <BetaBanner />
        <RealtimeBanner />
      </div>
    </>
  );
};

export default App;
